/**
 * #663 · **De qué sesión es cada petición, en un solo sitio.**
 *
 * Hasta aquí, que una respuesta tardía no pintara nada en la sesión siguiente dependía de cada
 * pantalla: carriles de lecturas, sellos de sesión, guardas de escritura… y bastaba con añadir una
 * llamada sin ellos para que el éxito, el error o el `finally` de la cuenta A cayeran sobre la B
 * —un aviso, un popup que se cierra, un preloader que se apaga—. Esta es la frontera común: la
 * cruzan `DatabaseModel.sendRequest`, `DatabaseModel.peticionDirecta` y los retornos de la caché,
 * y lo que no la cruce lo encuentra el guardarraíl de `scripts/prueba-frontera-sesion.js`.
 *
 * ## Generación, no token
 *
 * La identidad no puede ser el token (`identidadSesion`): renovarlo dentro de la MISMA sesión —el
 * reingreso automático de Cordova con las credenciales guardadas— lo cambia, y tiraría peticiones
 * buenas. Tampoco el usuario: cerrar y volver a entrar con la MISMA cuenta tiene que invalidar lo
 * pendiente igual que entrar con otra. Así que es un contador que avanza al cerrar sesión y al
 * abrir una nueva, y que no avanza al renovar.
 *
 * ## Qué pasa con lo pendiente
 *
 * Al avanzar, toda espera de la generación anterior **termina** rechazando con una cancelación
 * reconocible (`esCancelacion`): no se queda colgada ni bloquea los cerrojos de nadie. Y las
 * continuaciones que la pantalla colgó de ella (`then`, `catch`, `finally`) **no se ejecutan**:
 * ese es el código que pintaría, avisaría o soltaría el preloader de la sesión nueva. La
 * cancelación sigue la cadena hasta el final y allí la recoge `instalarRecogidaDeCancelaciones`,
 * así que tampoco deja un rechazo sin tratar en la consola.
 *
 * Descartar la respuesta **no deshace nada**: una escritura que el servidor ya recibió queda
 * aplicada. Aquí solo se calla su efecto en la interfaz, y no se reenvía nunca.
 *
 * ## La infraestructura sí se entera
 *
 * Callar las continuaciones vale para la pantalla, pero no para el código que cuelga de la
 * petición su propia contabilidad: el adaptador que envuelve la llamada en otra promesa y solo la
 * termina desde su `then`/`catch`, el carril que da de baja lo que espera, el ayudante de la acción
 * que suelta su cerrojo, la barrera que apaga su temporizador. Callado, eso se queda colgado o
 * cogido para siempre, y es justo lo que la sesión siguiente no puede heredar. Ese código adopta la
 * espera con `comoPromesaComun` —recibe la cancelación como un rechazo y decide qué soltar— o, si
 * construye su propia promesa, la ata con `atarALaSesion` para que termine igual que la de dentro.
 */

let generacion = 0;
// Las esperas de la generación vigente, para terminarlas al avanzar. Cada una se da de baja al
// contestar, así que esto solo crece con lo que de verdad está en vuelo.
const enVuelo = new Set();
const MARCA = '__cancelacionDeSesion663';
// Lo que tiene que soltar algo suyo cuando cambia la sesión (`alCambiarDeSesion`).
const oyentes = new Set();

export function generacionActual() {
    return generacion;
}

export function sigueLaGeneracion(gen) {
    return gen === generacion;
}

function avanzar() {
    generacion += 1;
    const pendientes = Array.from(enVuelo);
    enVuelo.clear();
    pendientes.forEach(function(cancelar) { cancelar(); });
    Array.from(oyentes).forEach(function(oyente) {
        try {
            oyente(generacion);
        } catch (e) {
            console.error('sesionDePeticiones: un oyente del cambio de sesión ha lanzado', e);
        }
    });
}

/**
 * Para lo que guarda algo de la sesión fuera de cualquier petición —los testigos del indicador de
 * espera— y lo tiene que dar por perdido cuando cambia. Renovar el token no avisa. Devuelve la baja.
 */
export function alCambiarDeSesion(oyente) {
    oyentes.add(oyente);
    return function() { oyentes.delete(oyente); };
}

/**
 * `saveSession`. Entrar —también con la misma cuenta que acaba de salir— es una sesión nueva; solo
 * `renovacion` (el reingreso con credenciales guardadas, sin pasar por el login) conserva lo
 * pendiente, porque sigue siendo la misma persona en la misma sesión.
 */
export function alAbrirSesion(opciones) {
    if (opciones && opciones.renovacion) return;
    avanzar();
}

/** `removeSession`: venga del botón, de un 401 o de otra pestaña. */
export function alCerrarSesion() {
    avanzar();
}

/**
 * La cancelación con la que termina una espera de otra sesión. Lleva `sesionAjena`, la marca que
 * ya miraban las pantallas para no avisar de un 401 de otra sesión, y no lleva `message`: si algo
 * llegara a enseñarla, no hay texto de la cuenta anterior que enseñar.
 */
export function cancelacion() {
    const e = { cancelada: true, sesionAjena: true, status: null, message: '' };
    Object.defineProperty(e, MARCA, { value: true });
    return e;
}

export function esCancelacion(e) {
    return !!e && typeof e === 'object' && e[MARCA] === true;
}

// Las funciones de resolución que pasa el motor —un `await`, `Promise.all`, `Promise.race`— no son
// código de pantalla: hay que llamarlas para que esa espera también termine. Se reconocen por ser
// nativas y sin nombre; los métodos de Vue también son nativos (`bind`), pero se llaman «bound …».
function esDelMotor(fn) {
    return !fn.name && /\[native code\]/.test(Function.prototype.toString.call(fn));
}

function guardar(fn, gen) {
    if (typeof fn !== 'function' || esDelMotor(fn)) return fn;
    return function(valor) {
        if (gen !== generacion) throw esCancelacion(valor) ? valor : cancelacion();
        return fn(valor);
    };
}

/**
 * La promesa que devuelve la frontera. Es una `Promise` de verdad —`instanceof`, `await` y
 * `Promise.all` la tratan como tal—, pero cada continuación que se le cuelga comprueba antes de
 * ejecutarse que la sesión que lanzó la operación sigue siendo la vigente. Lo que se encadena
 * hereda la misma comprobación, así que da igual cuántos `then` lleve detrás.
 */
export class PromesaDeSesion extends Promise {
    then(alCumplir, alRechazar) {
        const gen = this.generacion;
        const derivada = super.then(guardar(alCumplir, gen), guardar(alRechazar, gen));
        derivada.generacion = gen;
        return derivada;
    }

    // `finally` del motor envuelve el callback en funciones nativas propias y se saltaría la
    // guarda: es justo el que suelta preloaders y cerrojos, así que se escribe a mano.
    finally(alTerminar) {
        if (typeof alTerminar !== 'function') return this.then(alTerminar, alTerminar);
        return this.then(
            function(valor) { return Promise.resolve(alTerminar()).then(function() { return valor; }); },
            function(e) { return Promise.resolve(alTerminar()).then(function() { throw e; }); }
        );
    }
}

/**
 * Ata una promesa a la generación `gen` (la vigente al LANZAR, no al contestar). Si la sesión
 * cambia antes de que conteste, la espera termina en ese momento con la cancelación; si contesta
 * tarde, su resultado se cambia por la cancelación. En los dos casos no corre nada de la pantalla.
 */
export function atarALaSesion(promesa, gen) {
    if (gen === undefined) gen = generacion;
    let cancelar;
    const atada = new PromesaDeSesion(function(resolve, reject) {
        if (gen !== generacion) {
            reject(cancelacion());
            return;
        }
        cancelar = function() { reject(cancelacion()); };
        enVuelo.add(cancelar);
        Promise.resolve(promesa).then(
            function(valor) {
                enVuelo.delete(cancelar);
                if (gen !== generacion) reject(cancelacion());
                else resolve(valor);
            },
            function(e) {
                enVuelo.delete(cancelar);
                reject(gen !== generacion && !esCancelacion(e) ? cancelacion() : e);
            }
        );
    });
    atada.generacion = gen;
    return atada;
}

/**
 * La misma espera como `Promise` corriente: sus continuaciones se ejecutan SIEMPRE, y la de otra
 * sesión llega como rechazo con la cancelación (`esCancelacion`). Es para la infraestructura que
 * tiene que soltar lo suyo pase lo que pase (ver la cabecera), **no para pantallas**: lo que cuelgue
 * de aquí tiene que mirar `esCancelacion` antes de pintar o avisar.
 *
 * La adopción la hacen las funciones de resolución del motor, que la guarda deja pasar. Lo que no
 * sale de la frontera se devuelve tal cual: no hay nada que adoptar, y así no cambia ni su orden.
 */
export function comoPromesaComun(promesa) {
    return promesa instanceof PromesaDeSesion ? Promise.resolve(promesa) : promesa;
}

/**
 * La cancelación llega hasta el final de la cadena sin que nadie la atienda —a propósito: atenderla
 * sería ejecutar código de la pantalla—. Esto la recoge ahí para que no salga como «Uncaught (in
 * promise)». Cualquier otro rechazo sin tratar sigue saliendo como siempre.
 */
export function instalarRecogidaDeCancelaciones(destino) {
    const w = destino || (typeof window != 'undefined' ? window : null);
    if (!w || typeof w.addEventListener != 'function' || w[MARCA]) return;
    w[MARCA] = true;
    w.addEventListener('unhandledrejection', function(evento) {
        if (evento && esCancelacion(evento.reason)) evento.preventDefault();
    });
}
